import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { ChevronDown, ChevronRight, CircleHelp, LogOut, Settings, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { initials, ROLE_LABELS } from "@/lib/format";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const EXTERNAL_ROLES = ["EXTERNAL_ARCHITECT", "EXTERNAL_DESIGNER"];

// Profile, settings and help pages don't exist yet; these match the sidebar's placeholders.
const LINKS = [
  { label: "My Profile", icon: UserRound, testId: "user-menu-profile", onSelect: () => toast("My profile — coming soon") },
  { label: "Settings", icon: Settings, testId: "user-menu-settings", onSelect: () => toast("Settings — coming soon") },
  { label: "Help & Support", icon: CircleHelp, testId: "user-menu-help", onSelect: () => toast("Help center — coming soon") },
];

function Initials({ name, className }) {
  return (
    <span className={cn("rounded-full bg-accent/15 text-accent font-medium flex items-center justify-center shrink-0", className)}>
      {initials(name)}
    </span>
  );
}

export default function UserMenu() {
  const { user, logout, isPlatformAdmin } = useAuth();
  const roles = user?.roles || [];
  const roleLabel = ROLE_LABELS[roles[0]] || "";
  const userType = isPlatformAdmin() ? null : roles.some((r) => EXTERNAL_ROLES.includes(r)) ? "External Partner" : "Internal User";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="group flex items-center gap-2.5 rounded-full p-1 sm:pr-3 hover:bg-secondary data-[state=open]:bg-secondary transition-colors"
        >
          <Initials name={user?.name} className="h-9 w-9 text-xs" />
          <span className="hidden md:block text-left leading-tight">
            <span className="block text-sm font-medium text-foreground">{user?.name}</span>
            {roleLabel && <span className="block text-xs text-muted-foreground">{roleLabel}</span>}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden sm:block transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        data-testid="user-menu-panel"
        align="end"
        sideOffset={10}
        collisionPadding={12}
        className="w-[min(22rem,calc(100vw-1.5rem))] p-0 rounded-2xl border-border bg-card shadow-[0_18px_40px_-12px_rgba(42,33,29,0.25)]"
      >
        <DropdownMenuPrimitive.Arrow width={18} height={9} className="fill-card" />

        <div className="flex items-center gap-4 px-5 py-5 border-b border-border">
          <Initials name={user?.name} className="h-16 w-16 text-xl font-serif" />
          <div className="min-w-0">
            <p className="font-serif text-xl leading-tight text-foreground truncate">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {roleLabel && <span className="rounded-full bg-accent/10 text-accent px-2.5 py-0.5 text-xs font-medium">{roleLabel}</span>}
              {userType && <span className="rounded-full bg-secondary text-foreground/75 px-2.5 py-0.5 text-xs">{userType}</span>}
            </div>
          </div>
        </div>

        <div className="p-2">
          {LINKS.map(({ label, icon: Icon, testId, onSelect }) => (
            <DropdownMenuItem
              key={label}
              data-testid={testId}
              onSelect={onSelect}
              className="gap-3.5 rounded-lg px-3 py-2.5 text-[15px] text-foreground cursor-pointer focus:bg-secondary"
            >
              <Icon className="h-[18px] w-[18px] text-foreground/80" strokeWidth={1.6} />
              <span className="flex-1">{label}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </DropdownMenuItem>
          ))}
        </div>

        <div className="px-3 pb-3 pt-2 border-t border-border mx-2">
          <DropdownMenuItem
            data-testid="logout-menu-item"
            onSelect={logout}
            className="justify-center gap-2.5 rounded-xl bg-danger/[0.07] py-2.5 text-[15px] font-medium text-danger cursor-pointer focus:bg-danger/15 focus:text-danger"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} /> Log out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
